import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { PALETTES, type Palette, type ThemeId } from "@chess/shared";

const STORAGE_KEY = "gambit-theme";

interface ThemeCtx {
  themeId: ThemeId;
  palette: Palette;
  setThemeId: (id: ThemeId) => void;
}

const ThemeContext = createContext<ThemeCtx | null>(null);

function readStoredTheme(): ThemeId {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "classic" || raw === "soft" || raw === "vice") return raw;
  } catch {
    /* ignore */
  }
  return "classic";
}

interface Props {
  children: ReactNode;
  initial?: ThemeId | null;
  onPersist?: (id: ThemeId) => void;
}

export function ThemeProvider({ children, initial, onPersist }: Props) {
  const [themeId, setThemeIdState] = useState<ThemeId>(() => initial ?? readStoredTheme());

  useEffect(() => {
    if (initial && initial !== themeId) setThemeIdState(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const setThemeId = useCallback(
    (id: ThemeId) => {
      setThemeIdState(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        /* ignore */
      }
      onPersist?.(id);
    },
    [onPersist],
  );

  const palette = PALETTES[themeId];

  useEffect(() => {
    document.body.style.background = palette.bg;
    document.documentElement.style.background = palette.bg;
  }, [palette]);

  const value = useMemo<ThemeCtx>(() => ({ themeId, palette, setThemeId }), [themeId, palette, setThemeId]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Palette {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme outside ThemeProvider");
  return ctx.palette;
}

export function useThemeControls(): { themeId: ThemeId; setThemeId: (id: ThemeId) => void } {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeControls outside ThemeProvider");
  return { themeId: ctx.themeId, setThemeId: ctx.setThemeId };
}
